import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { fn } from '@ember/helper';
import { eq } from 'ember-truth-helpers';
import type { IntlService } from 'ember-intl';
import {
  googleMapsUrl,
  hasCoordinates,
  openStreetMapSingleEmbedUrl,
  openStreetMapUrl,
  staticMapUrl,
} from '../../utils/map';
import {
  flagUrl,
  flagUrl2x,
  formatRating,
  priceSymbol,
} from '../../utils/format';
import mapClickOverlay from '../../modifiers/map-click-overlay';
import tw from '../../helpers/tw';

interface Signature {
  Args: {
    data: Record<string, unknown> | null | undefined;
    distance?: string | null;
  };
}

interface PlaceData {
  name?: string;
  formattedAddress?: string;
  editorialSummary?: string;
  rating?: number;
  userRatingCount?: number;
  priceLevel?: string | number;
  primaryType?: string;
  countryCode?: string;
  googleMapsUrl?: string;
  websiteUrl?: string;
  photoUrl?: string;
  photoUrls?: string[];
  location?: {
    latitude: number;
    longitude: number;
  };
}

function asPlaceData(
  raw: Record<string, unknown> | null | undefined
): PlaceData {
  return raw ?? {};
}

export default class PlaceDetail extends Component<Signature> {
  @service declare intl: IntlService;

  @tracked currentPhotoIndex = 0;

  get data(): PlaceData {
    return asPlaceData(this.args.data);
  }

  @action
  previousPhoto(): void {
    const photos = this.displayPhotos;
    if (photos.length <= 1) return;
    this.currentPhotoIndex =
      (this.currentPhotoIndex - 1 + photos.length) % photos.length;
  }

  @action
  nextPhoto(): void {
    const photos = this.displayPhotos;
    if (photos.length <= 1) return;
    this.currentPhotoIndex = (this.currentPhotoIndex + 1) % photos.length;
  }

  @action
  goToPhoto(index: number): void {
    this.currentPhotoIndex = index;
  }

  get currentPhotoUrl(): string | undefined {
    return this.displayPhotos[this.currentPhotoIndex];
  }

  get hasMultiplePhotos(): boolean {
    return this.displayPhotos.length > 1;
  }

  get formattedDistance(): string | null {
    const distance = this.args.distance;
    if (!distance) return null;

    // Parse distance value and unit from the formatted string
    const match = distance.match(/^([\d.]+)\s*(m|km)$/);
    if (!match) return distance;

    const value = parseFloat(match[1]!);
    const unit = match[2]!;

    // Format number with thousand separators using ember-intl
    const formattedValue = this.intl.formatNumber(value, {
      minimumFractionDigits: 0,
      maximumFractionDigits: value < 10 && !Number.isInteger(value) ? 1 : 0,
    });

    // Combine with unit using translation
    if (unit === 'm') {
      return this.intl.t('common.distance_meters', {
        count: formattedValue,
      });
    }
    return this.intl.t('common.distance_kilometers', {
      count: formattedValue,
    });
  }

  get displayPhotos(): string[] {
    const d = this.data;
    // Prefer photoUrls array if available
    if (d.photoUrls && d.photoUrls.length > 0) {
      return d.photoUrls;
    }
    // Fall back to single photoUrl
    if (d.photoUrl) {
      return [d.photoUrl];
    }
    // Fall back to static map if coordinates available
    if (hasCoordinates(d.location)) {
      return [staticMapUrl(d.location.latitude, d.location.longitude)];
    }
    return [];
  }

  <template>
    {{#let this.data as |d|}}
      <div class="flex flex-col gap-4">
        {{#if this.displayPhotos.length}}
          <div class="carousel bg-base-200 relative rounded-lg">
            <div class="carousel-item">
              <img
                src={{this.currentPhotoUrl}}
                alt={{d.name}}
                class="h-56 w-full object-cover"
                loading="lazy"
              />
            </div>

            {{#if this.hasMultiplePhotos}}
              <button
                type="button"
                class="btn btn-circle btn-ghost bg-base-100/80 hover:bg-base-100 absolute top-1/2 left-2 -translate-y-1/2"
                {{on "click" this.previousPhoto}}
              >
                ❮
              </button>
              <button
                type="button"
                class="btn btn-circle btn-ghost bg-base-100/80 hover:bg-base-100 absolute top-1/2 right-2 -translate-y-1/2"
                {{on "click" this.nextPhoto}}
              >
                ❯
              </button>
              <div
                class="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-2"
              >
                {{#each this.displayPhotos as |_photo index|}}
                  <button
                    type="button"
                    class={{tw
                      "h-2 w-2 rounded-full transition-colors"
                      (if
                        (eq index this.currentPhotoIndex)
                        "bg-primary"
                        "bg-base-content/50"
                      )
                    }}
                    {{on "click" (fn this.goToPhoto index)}}
                  ></button>
                {{/each}}
              </div>
            {{/if}}
          </div>
        {{/if}}

        {{#if (hasCoordinates d.location)}}
          <div
            class="relative h-40 w-full overflow-hidden rounded-lg"
            {{mapClickOverlay}}
          >
            <iframe
              title={{d.name}}
              src={{openStreetMapSingleEmbedUrl
                d.location.latitude
                d.location.longitude
              }}
              class="h-full w-full border-0"
              loading="lazy"
            ></iframe>
          </div>
        {{/if}}

        {{#if d.formattedAddress}}
          <p class="text-base-content/80 text-sm">
            {{#if d.countryCode}}
              <img
                src={{flagUrl d.countryCode}}
                srcset="{{flagUrl d.countryCode}} 1x, {{flagUrl2x
                  d.countryCode
                }} 2x"
                width="16"
                alt={{d.countryCode}}
                class="mr-1 inline-block rounded-sm align-text-bottom shadow-sm"
              />
            {{else}}
              📍
            {{/if}}
            {{d.formattedAddress}}
            {{#if this.formattedDistance}}
              <span
                class="text-base-content/60 ml-2"
              >({{this.formattedDistance}})</span>
            {{/if}}
          </p>
        {{/if}}

        {{#if d.editorialSummary}}
          <p
            class="text-base-content/70 text-sm italic"
          >{{d.editorialSummary}}</p>
        {{/if}}

        <div class="flex flex-wrap gap-2">
          {{#if d.rating}}
            <div class="flex items-center gap-1">
              <span class="text-warning font-bold">★</span>
              <span class="text-sm font-semibold">{{formatRating
                  d.rating
                }}</span>
              {{#if d.userRatingCount}}
                <span
                  class="text-base-content/50 text-xs"
                >({{d.userRatingCount}})</span>
              {{/if}}
            </div>
          {{/if}}
          {{#if d.priceLevel}}
            <span class="badge badge-outline">{{priceSymbol
                d.priceLevel
              }}</span>
          {{/if}}
          {{#if d.primaryType}}
            <span
              class="badge badge-ghost badge-sm capitalize"
            >{{d.primaryType}}</span>
          {{/if}}
        </div>

        <div class="flex flex-wrap gap-2">
          {{#if d.googleMapsUrl}}
            <a
              href={{d.googleMapsUrl}}
              target="_blank"
              rel="noopener noreferrer"
              class="btn btn-xs btn-outline"
            >
              Google Maps ↗
            </a>
          {{else if (hasCoordinates d.location)}}
            <a
              href={{googleMapsUrl d.location.latitude d.location.longitude}}
              target="_blank"
              rel="noopener noreferrer"
              class="btn btn-xs btn-outline"
            >
              Google Maps ↗
            </a>
            <a
              href={{openStreetMapUrl d.location.latitude d.location.longitude}}
              target="_blank"
              rel="noopener noreferrer"
              class="btn btn-xs btn-outline"
            >
              OpenStreetMap ↗
            </a>
          {{/if}}
          {{#if d.websiteUrl}}
            <a
              href={{d.websiteUrl}}
              target="_blank"
              rel="noopener noreferrer"
              class="btn btn-xs btn-outline"
            >
              Website ↗
            </a>
          {{/if}}
        </div>
      </div>
    {{/let}}
  </template>
}
